/**
 * useChessGame - Game state management and derived state for chess
 *
 * Handles:
 * - Player color determination
 * - Turn management
 * - Game status (waiting/ongoing/completed)
 * - Move validation helpers
 * - Algebraic notation conversion
 */

import { useMemo, useCallback } from 'react';
import { ChessGame, ChessColor, SquarePosition, ChessPiece, Move } from '../types';
import { getPlayerColor as libGetPlayerColor, ChessColor as LibChessColor } from '../ChessLib';

export type GameStatus = 'loading' | 'error' | 'waiting' | 'ongoing' | 'completed';

export interface UseChessGameOptions {
    game: ChessGame | null;
    playerId: string | null;
    joined: boolean;
}

export interface UseChessGameReturn {
    // Game state
    game: ChessGame | null;
    gameStatus: GameStatus;
    isLoading: boolean;

    // Player info
    myColor: ChessColor | null;
    isMyTurn: boolean;
    amIWhite: boolean;
    amIBlack: boolean;

    // Players
    whitePlayer: ChessGame['players'][0] | null;
    blackPlayer: ChessGame['players'][0] | null;
    opponent: ChessGame['players'][0] | null;

    // Game result
    isGameOver: boolean;
    didIWin: boolean;
    didIDraw: boolean;
    winnerId: string | null;
    endReason: string | null;

    // Join state
    canJoin: boolean;
    canJoinAsWhite: boolean;
    canJoinAsBlack: boolean;

    // Helpers
    toAlgebraicNotation: (row: number, col: number) => string;
    createMove: (from: SquarePosition, to: SquarePosition) => Move;
}

export function useChessGame({
    game,
    playerId,
    joined
}: UseChessGameOptions): UseChessGameReturn {

    // Game status
    const gameStatus = useMemo((): GameStatus => {
        if (!game) return 'loading';
        if (game.state === 'waiting for players' || game.state === 'waiting') return 'waiting';
        if (game.state === 'ongoing') return 'ongoing';
        if (game.state === 'completed' || game.state === 'draw' || game.state.includes('-wins')) return 'completed';
        return 'loading';
    }, [game?.state]);

    const isLoading = gameStatus === 'loading';
    const isGameOver = gameStatus === 'completed';

    // Player references
    const whitePlayer = useMemo(() =>
        game?.players.find(p => p.color === ChessColor.PLAIN) || null,
    [game?.players]);

    const blackPlayer = useMemo(() =>
        game?.players.find(p => p.color === ChessColor.CHARCOAL) || null,
    [game?.players]);

    // My color
    const myColor = useMemo((): ChessColor | null => {
        if (!game || !playerId) return null;
        const player = game.players.find(p => p.id === playerId);
        return player?.color || null;
    }, [game, playerId]);

    const amIWhite = myColor === ChessColor.PLAIN;
    const amIBlack = myColor === ChessColor.CHARCOAL;

    // Opponent
    const opponent = useMemo(() => {
        if (!game || !playerId) return null;
        return game.players.find(p => p.id !== playerId) || null;
    }, [game, playerId]);

    // Turn management
    const isMyTurn = useMemo(() => {
        if (!game || !playerId || gameStatus !== 'ongoing') return false;
        return game.currentPlayer === playerId;
    }, [game, playerId, gameStatus]);

    // Game result
    const winnerId = game?.winnerId || null;
    const endReason = game?.endReason || null;

    const didIWin = useMemo(() => {
        if (!isGameOver || !playerId) return false;
        return winnerId === playerId;
    }, [isGameOver, playerId, winnerId]);

    const didIDraw = useMemo(() => {
        if (!isGameOver) return false;
        return game?.state === 'draw' || (!winnerId && isGameOver);
    }, [isGameOver, game?.state, winnerId]);

    // Join state
    const canJoin = useMemo(() => {
        if (joined) return false;
        if (!game) return false;
        if (game.players.length >= 2) return false;
        return true;
    }, [joined, game]);

    const canJoinAsWhite = useMemo(() =>
        canJoin && !whitePlayer,
    [canJoin, whitePlayer]);

    const canJoinAsBlack = useMemo(() =>
        canJoin && !blackPlayer && !game?.autoplay,
    [canJoin, blackPlayer, game?.autoplay]);

    // Algebraic notation conversion (considers board orientation)
    const toAlgebraicNotation = useCallback((row: number, col: number): string => {
        const columns = ['a', 'b', 'c', 'd', 'e', 'f', 'g', 'h'];

        // If playing as black, board is flipped
        if (amIBlack) {
            return `${columns[col]}${row + 1}`;
        }
        // White (default) - standard orientation
        return `${columns[col]}${8 - row}`;
    }, [amIBlack]);

    // Create move from square positions
    const createMove = useCallback((from: SquarePosition, to: SquarePosition): Move => {
        return {
            from: toAlgebraicNotation(from.row, from.col),
            to: toAlgebraicNotation(to.row, to.col)
        };
    }, [toAlgebraicNotation]);

    return {
        // Game state
        game,
        gameStatus,
        isLoading,

        // Player info
        myColor,
        isMyTurn,
        amIWhite,
        amIBlack,

        // Players
        whitePlayer,
        blackPlayer,
        opponent,

        // Game result
        isGameOver,
        didIWin,
        didIDraw,
        winnerId,
        endReason,

        // Join state
        canJoin,
        canJoinAsWhite,
        canJoinAsBlack,

        // Helpers
        toAlgebraicNotation,
        createMove
    };
}
